<template>
  <div class="px-0">
    <JRow v-if="!isMobile">
      <JCol class="mb-12">
        <span class="text-subject font-bold">
          {{ t('profile.chooseYourInterestsAndActivities') }}
        </span>
      </JCol>
    </JRow>
    <JRow v-if="isMobile" class="mb-12">
      <JCol
        v-for="interestAndActivity in interests"
        :key="interestAndActivity.label"
        class="mb-12"
      >
        <div class="flex flex-col gap-8">
          <div
            class="flex justify-between rounded-md p-16 shadow-md"
            @click="toggleInterest(interestAndActivity)"
          >
            <span class="text-subject text-gray-500">
              {{ $t(`profile.${interestAndActivity.label}`) }}
            </span>

            <JCheckBox
              color="primary"
              :modelValue="interestAndActivity.isSelected"
            />
          </div>
        </div>
      </JCol>
    </JRow>

    <div v-else class="mb-10 flex flex-wrap justify-between gap-4">
      <JChip
        v-for="(interestAndActivity, index) in interests"
        :key="interestAndActivity.indicator"
        variant="outlined"
        :color="interestAndActivity.isSelected ? 'primary' : 'gray-600'"
        size="lg"
        as="button"
        type="button"
        rounded="xl"
        class="font-medium"
        :class="[
          {
            'ltr:mr-auto rtl:ml-auto': index === interests.length - 1,
            '!bg-primary text-white': interestAndActivity.isSelected,
            '!border-line text-gray-600': !interestAndActivity.isSelected,
          },
        ]"
        :label="$t(`profile.${interestAndActivity.label}`)"
        @click="toggleInterest(interestAndActivity)"
      />
    </div>

    <JRow>
      <JCol class="text-right">
        <JButton
          :disabled="!isChanged"
          size="md"
          :block="isMobile"
          :isLoading="isLoading"
          @click="submit"
        >
          {{ t('words.save') }}
        </JButton>
      </JCol>
    </JRow>
  </div>
</template>

<script setup lang="ts">
import isEqual from 'lodash/isEqual'
import { computed, inject, ref, unref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { JButton, JCheckBox, JChip, JCol, JRow } from '@journey/ui-kit'
import type { Props } from './MyInterestsAndActivitiesFormFields.types'
import type {
  InterestAndActivityItem,
  OrderedUserInterestAndActivityItem,
} from '@/modules/Profile/types/model/interestsAndActivities'
import useProfileStore from '@/modules/Profile/store'
import {
  MY_INTERESTS_AND_ACTIVITIES_FORM_INJECTION_KEY,
  updateProfileSectionSharedStateInjectionKey,
} from '@/modules/Profile/constants'
import useDevicePlatform from '@/modules/Core/composables/useDevicePlatform'
import useSearchPanelStore from '@/modules/Search/store/searchPanel'

const props = defineProps<Props>()

const internalUserInterestsAndActivities = ref<
  OrderedUserInterestAndActivityItem[]
>([...props.userInterestsAndActivities])

// I18N
const { t } = useI18n()

// MODEL
function findInterestAncActivityIndex(
  interestAndActivity: InterestAndActivityItem
) {
  return internalUserInterestsAndActivities.value.findIndex(
    (i) => i.number === interestAndActivity.number
  )
}

function deselectInterestAnsActivity(selectedInterest: number) {
  internalUserInterestsAndActivities.value.splice(selectedInterest, 1)
}

function selectInterestAndActivity(
  interestAndActivity: InterestAndActivityItem
) {
  internalUserInterestsAndActivities.value.push({
    indicator: null,
    label: interestAndActivity.label,
    number: interestAndActivity.number,
    orderId: interestAndActivity.number,
  })
}

function toggleInterest(interestAndActivity: InterestAndActivityItem) {
  const index = findInterestAncActivityIndex(interestAndActivity)

  if (index >= 0) {
    deselectInterestAnsActivity(index)

    return
  }

  selectInterestAndActivity(interestAndActivity)
}

const interests = computed(() =>
  props.interestsAndActivities.map((item) => {
    const isSelected = internalUserInterestsAndActivities.value.some(
      (i) => i.number === item.number
    )

    return {
      ...item,
      isSelected,
    }
  })
)

// RESPONSIVE
const { isMobile } = useDevicePlatform()

// SUBMISSION
const updateProfileSectionSharedState = inject(
  updateProfileSectionSharedStateInjectionKey
)

function generatePayload() {
  const payload: Omit<InterestAndActivityItem, 'indicator'>[] = []

  internalUserInterestsAndActivities.value.forEach((interestAndActivity) => {
    payload.push({
      number: interestAndActivity.number,
      label: interestAndActivity.label,
    })
  })

  return payload
}

// DISABLED
const isChanged = ref(false)

watch(
  () => internalUserInterestsAndActivities.value.length,
  () => {
    isChanged.value = !isEqual(
      props.userInterestsAndActivities.map((v) => v.label).sort(),
      internalUserInterestsAndActivities.value.map((v) => v.label).sort()
    )
  }
)

watch(isChanged, (value) => {
  if (!updateProfileSectionSharedState) {
    return
  }

  updateProfileSectionSharedState.setTouched(
    MY_INTERESTS_AND_ACTIVITIES_FORM_INJECTION_KEY,
    value
  )
})

const searchPanelStore = useSearchPanelStore()

// SUBMIT
function submit() {
  if (!isChanged.value) {
    return
  }

  if (!updateProfileSectionSharedState) {
    return
  }

  const payload = generatePayload()

  updateProfileSectionSharedState.items[
    MY_INTERESTS_AND_ACTIVITIES_FORM_INJECTION_KEY
  ].submit(payload)

  // UPDATE SEARCH PANEL INTERESTS
  searchPanelStore.setInterestsAndActivities(payload.map(({ label }) => label))

  isChanged.value = false
}

const profileStore = useProfileStore()

watch(
  () => internalUserInterestsAndActivities.value.length,
  () => {
    profileStore.setConfirmationPayload(
      isChanged.value ? generatePayload() : null
    )
  }
)

// LOADING
const isLoading = computed(() => {
  if (!updateProfileSectionSharedState) {
    return
  }

  return unref(
    updateProfileSectionSharedState?.items[
      MY_INTERESTS_AND_ACTIVITIES_FORM_INJECTION_KEY
    ].isLoading
  )
})
</script>
